import React, {HTMLAttributes, useCallback, useEffect, useState} from "react";
import ReactModal from "react-modal";
import {Channel} from "./Channel/Channel";
import {ExternalUserProvider} from "../ExternalUserProvider";
import useChannelPreview from "../api/useChannelPreview";
import CommentRow from "./CommentRow";
import {Trans, useTranslation} from "react-i18next";
import {Comment} from "../api/Models";
import useChannelSettings from "../api/useChannelSettings";

import {Info, MessageSquare, Send} from "react-feather";
import { TrackingFunction } from "./TrackingFunction";

export interface ChannelPreviewProps {
  ownerId: string,
  channelId: string,
  version?: number,
  position?: number,
  externalUserProvider?: ExternalUserProvider,
  portalClassName?: string,
  portalStyle?: object
  onCommentManage?: (commentId: string) => void
  useCookies?: boolean
  trackingFunction?: TrackingFunction
}

interface CommentsContainerProps extends HTMLAttributes<HTMLDivElement>{
  position?: number
}

export function CommentsContainer({position, children, ...attributes} : CommentsContainerProps) {
  return <div className={`tik4-content-block tik4-content-block--comments ${position ? `tik4-content-block--position-${position}`: ''}`} {...attributes}>
    <div className="tik4-comments">
      {children}
    </div>
  </div>
}

function useModal(channelId: string): [boolean, (val: boolean) => void] {
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    const eventHandler = (event: any)  => {
      if(event.detail && event.detail === channelId){
        setShowModal(true);
      } else {
        setShowModal(false);
      }
    }
    document.addEventListener("tikShowCommentChannelModal", eventHandler);
    return () => document.removeEventListener("tikShowCommentChannelModal", eventHandler);
  }, [channelId]);

  function setShowUniqueModal(show: boolean) {
    if(show){
      document.dispatchEvent(new CustomEvent('tikShowCommentChannelModal', {detail: channelId}));
    }
    else {
      setShowModal(false);
    }
  }

  return [showModal, setShowUniqueModal]
}

export default function ChannelPreview({ownerId, channelId, version, position, externalUserProvider, onCommentManage, portalClassName, portalStyle, useCookies, trackingFunction}: ChannelPreviewProps){
  const {isLoading, total, data, hasNextPage, error, refetch} = useChannelPreview({ownerId, channelId, version});
  const {data: settings}  = useChannelSettings({ownerId, channelId, version: (version || 0)});
  const [showModal, setShowModal] = useModal(channelId);
  const {t} = useTranslation("liveblog");

  const portalClassNamesOrDefault = portalClassName || 'tik4 tik4--on-light'
  const portalStyleOrDefault = portalStyle || {}

  const hasPostButton = (settings && settings?.publishMode !== "ReadOnly")

  const handleModalClose = useCallback(() => {
    setShowModal(false);
  }, [setShowModal])

  const handleModalOpen = useCallback(() => {
    trackingFunction && trackingFunction({t: "c_opn"})
    setShowModal(true)
  }, [setShowModal, trackingFunction])

  const handleCommentCreated = useCallback((comment: Comment) => {
     if(comment.publishedAt){
       refetch();
     }
  }, [refetch])

  if(isLoading){
    return <CommentsContainer position={position}>
      <div className="tik4-comments__loader">
        {t('liveblog.comments.preview.loading')}
      </div>
    </CommentsContainer>;
  }
  if(error){
    // TODO improve messages
    throw Error("Could not load comments " + error);
  }

  function renderWriteComment(){
    if(!hasPostButton){
      return null;
    }
    let btnText;
    if(externalUserProvider && externalUserProvider.getCurrentUser()){
      btnText = t('liveblog.comments.button.comment.as', {username: externalUserProvider.getCurrentUser()!.name});
    } else {
      btnText = t('liveblog.comments.button.comment.generic');
    }
    //
    return <div className="tik4-comments__write">
      {/* TODO:Matthias Trigger login as well */}
      <button className="tik4-form-field tik4-form-field--sm tik4-comments__write__button" onClick={handleModalOpen} type="button">
        <span className="tik4-comments__write__button__placeholder">{btnText}</span>
        <Send className="tik4-icon tik4-comments__write__button__icon" />
      </button>

      {!settings?.allowAnonymousPost && externalUserProvider && !externalUserProvider.getCurrentUser() && <div className="tik4-hint tik4-hint--sm tik4-comments__write__hint">
        <Info className="tik4-icon" />
        <div className="tik4-hint__content">
          {externalUserProvider.triggerLogin ?
            <Trans i18nKey="liveblog.comments.login.with_button">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              Please <a href="#" className="tik4-link tik4-link--within-body" onClick={externalUserProvider.triggerLogin}>log in</a> to comment this message.
            </Trans>
            :
            t('liveblog.comments.login.no_button')
          }
        </div>

      </div>}

    </div>
  }

  return <CommentsContainer position={position}>
    <div className="tik4-comments__header">
      <div className="tik4-comments__header__label">
        <MessageSquare className="tik4-comments__header__label__icon" />
        <span className="tik4-comments__header__label__content">
          {t('liveblog.comments.preview.headline', {count: total})}
        </span>
      </div>
    </div>

    { renderWriteComment() }

    <div className="tik4-comments__list">
      {data.map((comment) =>
        <CommentRow key={comment.id} comment={comment} onCommentManage={onCommentManage}/>
      )}
    </div>
    {hasNextPage && <div className="tik4-more-comments">
      <button className="tik4-more-comments__button" onClick={handleModalOpen} type="button">
        {t('liveblog.comments.preview.show-all-comments', {count: total})}</button>
    </div>}
    <ReactModal
        ariaHideApp={false}
        portalClassName={`tik4-modal tik4-modal--bottom sm:tik4-modal--right lg:tik4-modal--right`}
        style={{content: portalStyleOrDefault}}
        bodyOpenClassName='sm:tik4-body--modal-open-scroll'
        htmlOpenClassName='tik4-html--modal-open sm:tik4-html--modal-open-scroll'
        className={{
          base: `tik4-modal__content tik4-modal__content--comments ${portalClassNamesOrDefault}`,
          afterOpen: 'tik4-modal__content--after-open',
          beforeClose: 'tik4-modal__content--before-close'
        }}
        overlayClassName={{
          base: 'tik4-modal__overlay tik4-modal__overlay--comments',
          afterOpen: 'tik4-modal__overlay--after-open',
          beforeClose: 'tik4-modal__overlay--before-close'
        }}
        isOpen={showModal}
        shouldCloseOnEsc={true}
        onRequestClose={handleModalClose}
      >
      <Channel
        ownerId={ownerId}
        channelId={channelId}
        externalUserProvider={externalUserProvider}
        onClose={handleModalClose}
        onCommentCreated={handleCommentCreated}
        onCommentManage={onCommentManage}
        version={version}
        useCookies={useCookies}
        trackingFunction={trackingFunction}
      />
    </ReactModal>

  </CommentsContainer>
}
